import {
  useMutation,
  useQueryClient,
  useQuery,
  useIsFetching,
} from "@tanstack/react-query";
import Select from "react-select";
import { useEffect, useCallback, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import SportTableMobile from "../../contrainers/SportTable/SportTableMobile";
import mutation from "../../mutation";
import BetTypeSelectorMobile from "../../contrainers/BetTypeSelector/BetTypeSelectorMobile";
import useScreen from "../../hooks/useScreen";
import NewSportTableContainer from "../../contrainers/SportTableLiveAndToday/NewSportTableContainer";
import BreadCrumb from "../../components/BreadCrumb";
import Refresh from "../../components/Refresh";
import query from "../../query";
import { getSportQueryKey } from "../../utils/queryKeyHelper";
import BetTypeSelector from "../../contrainers/BetTypeSelector";
import {
  SPORTS_WITHOUT_COMBO,
  SPORTS_WITHOUT_PALAY,
} from "../../constants/common";
import SearchInput from "../../components/SearchInput";
import SearchInputMobile from "../../components/SearchInputMobile";
import LeagueFilterModal from "../../contrainers/LeagueFilterModal";
import useLeagueFilterModal from "../../contrainers/LeagueFilterModal/useLeagueFilterModal";
import {
  LeagueFilterButtonMobile,
  LeagueFilterButton,
} from "../../components/LeagueFilterButton/LeagueFilterButtonMobile";
import { MatchSharePayload } from "../../types/table";
import { useTranslation } from "react-i18next";
import { CONFIG } from "../../config";
import { providerOptions } from "../../pages/MyBets/config";

const FootballPage = () => {
  const { sport = "football", type = "today" } = useParams<{
    sport:
      | "football"
      | "basketball"
      | "volleyball"
      | "tabletennis"
      | "handball"
      | "icehockey"
      | "tennis"
      | "snooker"
      | "baseball"
      | "rugby"
      | "americanfootball"
      | "cricket"
      | "boxing"
      | "bull_fight"
      | "cock_fight"
      | "badminton";
    type: "today" | "live" | "early";
  }>();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sharedMatchId = searchParams.get("matchId") || undefined;

  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [allTeamNames, setAllTeamNames] = useState<string[]>([]);
  const [leagueOptions, setLeagueOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const { t } = useTranslation();

  const {
    isToggled: isLeagueModalOpen,
    toggle: toggleLeagueModal,
    isAllSelected,
    selectedLeagues,
    onSubmit: onLeagueFormSubmit,
    setFilterState,
  } = useLeagueFilterModal();

  useEffect(() => {
    setSearchTerm("");
    setAllTeamNames([]);
    setFilterState({ isAllSelected: true, selectedLeagues: [] });
  }, [sport, type, setFilterState]);

  const updateBetType = useMutation(mutation.updateBetType, {
    onSuccess: (value) => {
      queryClient.setQueryData(["betType"], value);
    },
  });

  const updateOddsType = useMutation(mutation.updateOddsType, {
    onSuccess: (value) => {
      queryClient.setQueryData(["oddsType"], value);
    },
  });

  const updateBetTicketInfoPayload = useMutation(
    mutation.updateBetTicketInfoPayload,
    {
      onSuccess: (payload) => {
        queryClient.setQueryData(["betTicketInfoPayload"], payload);
      },
    },
  );

  const betTypeQuery = query.betType();
  const betType = useQuery(betTypeQuery.key, betTypeQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  const providerQuery = query.provider();
  const pvd = useQuery(providerQuery.key, providerQuery.queryFn, {
    refetchOnMount: false,
    keepPreviousData: true,
  });

  const updateProvider = useMutation(mutation.updateProvider, {
    onSuccess: (payload) => {
      queryClient.setQueryData(["provider"], payload);
    },
  });

  const currentQueryKey = useMemo(
    () => getSportQueryKey(sport, type, betType.data ?? "normal", pvd.data),
    [sport, type, betType.data, pvd],
  );

  const isFetching = useIsFetching({
    queryKey: currentQueryKey,
  });

  const handleRefetch = useCallback(() => {
    queryClient.refetchQueries({ queryKey: currentQueryKey });
  }, [queryClient, currentQueryKey]);

  const { isDesktop } = useScreen();

  useEffect(() => {
    updateBetTicketInfoPayload.mutate([]);
    updateBetType.mutate("normal");
    updateOddsType.mutate(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTeamsLoad = useCallback((teams: string[]) => {
    setAllTeamNames((prevTeams) => {
      const combined = new Set([...prevTeams, ...teams]);
      return Array.from(combined);
    });
  }, []);

  const handleShareMatch = useCallback(
    (match: MatchSharePayload) => {
      const title = `${match.homeName} vs ${match.awayName}`;
      const url = `${window.location.origin}/${sport}/${type}?matchId=${match.matchId}`;
      const text =
        match.kickoffTime ?
          `${title} ${t("share kickoff")} ${match.kickoffTime}\n${t("share link")}`
        : `${title}\n${t("shshare link")}`;

      if (navigator.share) {
        navigator.share({ title, text, url }).catch(console.error);
      } else {
        navigator.clipboard
          .writeText(`${text}\n${url}`)
          .then(() => alert(`${t("share copied")}`))
          .catch(() => alert(`${t("share copy_failed")}`));
      }
    },
    [sport, type, t],
  );

  return (
    <div className="mb-10 desktop:ml-3 desktop:mr-3">
      {isDesktop ?
        <div className="w-full mx-auto overflow-auto">
          <div className="w-full mx-auto">
            <div className="flex mb-4 items-center flex-wrap xl:flex-nowrap">
              <BreadCrumb path={`${sport}/${type}`} />
              <div className="relative mr-3">
                <SearchInput
                  allItems={allTeamNames}
                  onSearchChange={setSearchTerm}
                />
              </div>
              <div className="relative mr-3">
                <Refresh
                  className="ml-auto border border-solid border-blue-500 text-blue-500 rounded flex justify-center items-center"
                  style={{
                    width: 150,
                    minHeight: 30,
                  }}
                  key={type}
                  onClick={handleRefetch}
                  isLoading={isFetching > 0}
                  initialCounter={type === "live" ? 10 : 60}
                />
              </div>
              <BetTypeSelector
                sport={sport}
                isParlayDisabled={SPORTS_WITHOUT_PALAY.includes(sport)}
                isComboDisabled={SPORTS_WITHOUT_COMBO.includes(sport)}
              />
              <div className="relative" style={{ height: 30 }}>
                <LeagueFilterButton onClick={toggleLeagueModal} />
              </div>
            </div>
            {CONFIG.ENV === "develop" && (
              <div>
                <Select
                  options={providerOptions}
                  value={providerOptions.find(
                    (option) => pvd.data === option.value,
                  )}
                  isSearchable={false}
                  onChange={(option) => {
                    if (!option) {
                      return;
                    }
                    updateProvider.mutate(option.value);
                    updateBetTicketInfoPayload.mutate([]);
                  }}
                />
              </div>
            )}
          </div>
          <NewSportTableContainer
            type={type}
            sport={sport}
            searchTerm={searchTerm}
            onTeamsLoad={handleTeamsLoad}
            leagueFilterState={{ isAllSelected, selectedLeagues }}
            onLeaguesLoad={setLeagueOptions}
            onShareMatch={handleShareMatch}
            sharedMatchId={sharedMatchId}
            pvd={pvd.data}
          />
        </div>
      : <>
          {/* {sport === "football" && <TopEvent sport="football" />} */}
          <BetTypeSelectorMobile
            sport={sport}
            type={type}
            title={`${sport}/${type}`}>
            <div className="flex items-center gap-2 px-2 pb-4">
              <div className="flex-1">
                <SearchInputMobile
                  allItems={allTeamNames}
                  onSearchChange={setSearchTerm}
                />
              </div>
              <div>
                <LeagueFilterButtonMobile
                  className="py-2.5"
                  onClick={toggleLeagueModal}
                />
              </div>
            </div>
            {CONFIG.ENV === "develop" && (
              <div>
                <Select
                  options={providerOptions}
                  value={providerOptions.find(
                    (option) => pvd.data === option.value,
                  )}
                  isSearchable={false}
                  onChange={(option) => {
                    if (!option) {
                      return;
                    }
                    updateProvider.mutate(option.value);
                    updateBetTicketInfoPayload.mutate([]);
                  }}
                />
              </div>
            )}
            <SportTableMobile
              title={`${sport}/${type}`}
              type={type}
              sport={sport}
              searchTerm={searchTerm}
              onTeamsLoad={handleTeamsLoad}
              onLeaguesLoad={setLeagueOptions}
              leagueFilterState={{ isAllSelected, selectedLeagues }}
              onShareMatch={handleShareMatch}
              sharedMatchId={sharedMatchId}
            />
          </BetTypeSelectorMobile>
        </>
      }
      <LeagueFilterModal
        isDesktop={isDesktop}
        isToggled={isLeagueModalOpen}
        onClose={toggleLeagueModal}
        options={leagueOptions}
        defaultIsAllSelected={isAllSelected}
        defaultSelectedLeagues={selectedLeagues}
        onSubmit={(data) => {
          onLeagueFormSubmit(data);
        }}
      />
    </div>
  );
};

export default FootballPage;
